/**
 * Utility for simplifying recipe instructions to be more kid-friendly
 */

/**
 * Simplifies cooking instructions to be more kid-friendly
 */
export const simplifyInstruction = (instruction: string): string => {
  // Split into sentences and clean up each one
  const sentences = instruction
    .split(/(?<=[.!?])\s+/)
    .map(sentence => {
      // Remove text in parentheses
      sentence = sentence.replace(/\([^)]*\)/g, '');
      
      // Enhanced spelling corrections with more variations and common mistakes
      sentence = sentence
        // Common verbs - fix spelling and tense issues
        .replace(/mov(?:e|er|eing|ing)?/gi, 'move')
        .replace(/togetherd?/gi, 'together')
        .replace(/under(?:baking|cooking)d?/gi, 'underbaking')
        .replace(/untill?/gi, 'until')
        .replace(/allready|all?redy/gi, 'already')
        .replace(/sep(?:a|e)rat(?:e|ing)?/gi, 'separate')
        .replace(/rec(?:ie|ei)v(?:e|ing)?/gi, 'receive')
        .replace(/occur(?:r)?(?:ing)?/gi, 'occur')
        .replace(/trans(?:f)?fer(?:r)?(?:ing)?/gi, 'transfer')
        .replace(/stir(?:r)?(?:ing)?/gi, 'stir')
        .replace(/mix(?:x)?(?:ing)?g?/gi, 'mix')
        .replace(/beat(?:t)?(?:ing)?/gi, 'beat')
        .replace(/whisk(?:k)?(?:ing)?/gi, 'whisk')
        .replace(/fold(?:d)?(?:ing)?/gi, 'fold')
        .replace(/knead(?:d)?(?:ing)?/gi, 'knead')
        .replace(/bake(?:e)?(?:ing)?d?/gi, 'bake')
        .replace(/cool(?:l)?(?:ing)?/gi, 'cool')
        .replace(/pour(?:r)?(?:ing)?/gi, 'pour')
        .replace(/blend(?:d)?(?:ing)?/gi, 'blend')
        .replace(/chop(?:p)?(?:ing)?/gi, 'chop')
        .replace(/slice(?:e)?(?:ing)?/gi, 'slice')
        .replace(/dice(?:e)?(?:ing)?/gi, 'dice')
        .replace(/grat(?:te|e)?(?:ing)?/gi, 'grate')
        
        // Common adjectives and states
        .replace(/smooth(?:e|ly)?/gi, 'smooth')
        .replace(/care(?:e)?full?(?:ly)?/gi, 'carefully')
        .replace(/gentle(?:e)?l?(?:ly)?/gi, 'gently')
        .replace(/thorough(?:gh)?l?(?:ly)?/gi, 'thoroughly')
        .replace(/quick(?:k)?l?(?:ly)?/gi, 'quickly')
        .replace(/soft(?:t)?l?(?:ly)?/gi, 'softly')
        .replace(/firm(?:m)?l?(?:ly)?/gi, 'firmly')
        .replace(/light(?:t)?l?(?:ly)?/gi, 'lightly')
        .replace(/even(?:n)?l?(?:ly)?/gi, 'evenly')
        
        // Fix common grammatical mistakes specific to this recipe
        .replace(/\bbaking for\b/gi, 'bake for')
        .replace(/\boverbaking\b/gi, 'overbake')
        .replace(/\blet them cooling\b/gi, 'let them cool')
        .replace(/\bsoftly cookies\b/gi, 'soft cookies')
        .replace(/\bstay softly\b/gi, 'stay soft')
        .replace(/\bis essential for keeping the cookies softly\b/gi, 'is essential for keeping the cookies soft');
      
      // Simplify measurements to be more kid-friendly
      sentence = sentence
        .replace(/(\d+(\.\d+)?\/\d+|\d+)\s*(tablespoons?|tbsp)/gi, 'a big spoon')
        .replace(/(\d+(\.\d+)?\/\d+|\d+)\s*(teaspoons?|tsp)/gi, 'a small spoon')
        .replace(/(\d+(\.\d+)?\/\d+|\d+)\s*(cups?)/gi, 'a cup')
        .replace(/(\d+(\.\d+)?\/\d+|\d+)\s*(ounces?|oz)/gi, 'some')
        .replace(/(\d+(\.\d+)?\/\d+|\d+)\s*(grams?|g)/gi, 'some');
      
      // Keep the actual timing numbers
      sentence = sentence
        .replace(/(\d+)\s*(?:to|-)\s*(\d+)\s*(?:minutes?|hours?)/g, '$1 minutes')
        .replace(/(\d+)\s*(?:minutes?|hours?)/g, '$1 minutes');
      
      // Remove extra spaces
      sentence = sentence.replace(/\s+/g, ' ').trim();
      
      // Start each sentence with a capital letter
      return sentence.charAt(0).toUpperCase() + sentence.slice(1);
    })
    .filter(s => s.length > 0);

  // Join sentences back together
  return sentences
    .map(sentence => {
      // Make common cooking terms more kid-friendly while avoiding double terms
      return sentence
        .replace(/combine/gi, 'stir together')
        .replace(/incorporate/gi, 'blend')
        .replace(/gradually/gi, 'slowly')
        .replace(/until combined/gi, 'until it looks smooth')
        .replace(/refrigerate/gi, 'put in the fridge')
        .replace(/proceed/gi, 'continue')
        .replace(/approximately/gi, 'about')
        .replace(/transfer/gi, 'move')
        .replace(/distribute/gi, 'spread')
        .replace(/excess/gi, 'extra')
        .replace(/portion/gi, 'piece')
        .replace(/thoroughly/gi, 'well')
        .replace(/ensure/gi, 'make sure')
        .replace(/for a good (\d+) minutes/gi, 'for $1 minutes')
        .replace(/or so\b/gi, '')
        .replace(/that are the best in all the land/gi, 'that taste amazing')
        .replace(/this advice is probably written on every cookie recipe everywhere,/gi, '')
        .replace(/evenly if/gi, 'even if');
    })
    .join(' ');
};
